/**
 * GoogleContactsAdapter - Google Contacts API adapter
 * 
 * CHANGE HISTORY:
 * - 251030_0125 [pending] danielbeer: Moved to Interactr from Synkr (better architecture)
 * - 251029_2327 [pending] danielbeer: Google Contacts adapter using Interactr + Authr OAuth2
 * 
 * Uses:
 * - Interactr for HTTP requests
 * - Authr for OAuth2 authentication
 * - Google People API v1
 */

import { BaseApiAdapter } from './BaseApiAdapter.js';
import type { ContactChange } from './types.js';

export interface GoogleContactsConfig {
  interactr: any; // InteractrManager instance
  clientId: string;
  clientSecret: string;
  refreshToken?: string;
  keystoreRef?: {
    provider: string;
    path: string;
    key: string;
  };
}

export class GoogleContactsAdapter extends BaseApiAdapter {
  private config: GoogleContactsConfig;
  private interactr: any;
  private requestId: string | null = null;

  constructor(config: GoogleContactsConfig) {
    super('google-contacts');
    this.config = config;
    this.interactr = config.interactr;
  }

  /**
   * Initialize adapter - create Interactr request
   */
  async initialize(): Promise<void> {
    if (!this.interactr) {
      throw new Error('Interactr not configured');
    }

    // Create API request in Interactr
    const request = await this.interactr.createRequest({
      name: 'Google Contacts - List',
      method: 'GET',
      url: 'https://people.googleapis.com/v1/people/me/connections',
      queryParams: {
        personFields: 'names,emailAddresses,phoneNumbers,addresses,organizations,birthdays,photos',
        pageSize: '1000',
      },
      auth: {
        type: 'oauth2',
        config: {
          grantType: 'authorization_code',
          tokenUrl: 'https://oauth2.googleapis.com/token',
          clientId: this.config.clientId,
          clientSecret: this.config.clientSecret,
          scope: 'https://www.googleapis.com/auth/contacts.readonly',
          refreshToken: this.config.refreshToken,
        },
        keystoreRef: this.config.keystoreRef,
      },
      tags: ['google', 'contacts', 'sync'],
      createdBy: 'synkr',
      updatedBy: 'synkr',
    });

    this.requestId = request.id;
  }

  /**
   * Pull changes from Google Contacts
   */
  async pull(lastSync?: number): Promise<ContactChange[]> {
    if (!this.requestId) {
      await this.initialize();
    }

    try {
      // Execute request via Interactr (handles OAuth2 automatically)
      const response = await this.interactr.executeRequest(this.requestId);

      if (response.status !== 200) {
        throw new Error(`Google Contacts API error: ${response.status} ${response.statusText}`);
      }

      const data = response.body;
      const connections = data.connections || [];

      // Convert Google contacts to ContactChange format
      const changes: ContactChange[] = connections.map((contact) => {
        const resourceName = contact.resourceName;
        const id = resourceName.split('/').pop();

        // Extract contact data
        const names = contact.names?.[0];
        const emails = contact.emailAddresses || [];
        const phones = contact.phoneNumbers || [];
        const addresses = contact.addresses || [];
        const organizations = contact.organizations?.[0];
        const photos = contact.photos?.[0];

        const contactData = {
          id,
          resourceName,
          displayName: names?.displayName || '',
          firstName: names?.givenName || '',
          lastName: names?.familyName || '',
          emails: emails.map((e) => ({
            id: e.metadata?.source?.id || '',
            email: e.value,
            type: e.type?.toLowerCase() || 'other',
            isPrimary: e.metadata?.primary || false,
          })),
          phones: phones.map((p) => ({
            id: p.metadata?.source?.id || '',
            number: p.value,
            type: p.type?.toLowerCase() || 'other',
            isPrimary: p.metadata?.primary || false,
          })),
          addresses: addresses.map((a) => ({
            id: a.metadata?.source?.id || '',
            street: a.streetAddress || '',
            city: a.city || '',
            state: a.region || '',
            zip: a.postalCode || '',
            country: a.country || '',
            type: a.type?.toLowerCase() || 'other',
            isPrimary: a.metadata?.primary || false,
          })),
          company: organizations?.name || '',
          jobTitle: organizations?.title || '',
          photo: photos?.url || '',
          source: 'google',
          syncSource: 'google',
        };

        // Get last modified time
        const metadata = contact.metadata;
        const updatedTime = metadata?.sources?.[0]?.updateTime;
        const timestamp = updatedTime ? new Date(updatedTime).getTime() : Date.now();

        // Only include if modified after lastSync
        if (lastSync && timestamp <= lastSync) {
          return null;
        }

        return {
          id,
          type: 'update' as const,
          data: contactData,
          timestamp,
          source: 'google-contacts',
        };
      }).filter((c) => c !== null);

      return changes;
    } catch (error) {
      throw new Error(`Failed to pull from Google Contacts: ${error.message}`);
    }
  }

  /**
   * Push change to Google Contacts
   */
  async push(change: ContactChange): Promise<boolean> {
    // TODO: Implement push to Google Contacts
    // This requires creating/updating contacts via Google People API
    // For now, we'll just log that push is not yet implemented
    console.log(`Google Contacts push not yet implemented for change ${change.id}`);
    return false;
  }


  /**
   * Check if adapter is available
   */
  async isAvailable(): Promise<boolean> {
    return !!this.interactr;
  }
}
